import { ControlPointOutlined } from "@mui/icons-material";
import {
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";

import { inject } from "../../../../../../../../../../lib/itn-ui-kit/services/service-container";
import useRevalidateParent from "../../../../../../../../../hooks/useRevalidateParent";
import ApiRequestErrorHandler from "../../../../../../../../global/ApiRequestErrorHandler";
import FormSubmitButton from "../../../../../../../../global/FormComponents/FormSubmitButton";
import ResponseModal from "../../../../../../../../global/ResponseModal";

import InjuryAddRow from "./InjuryAddRow";
import InjuryUpdateRow from "./InjuryUpdateRow";

export default function InjuryForm({ injury, casePk }) {
  const caseInjuriesRepository = inject("repositories/case-injuries-repository");
  const currentUserService = inject("current-user-service");
  const canAddData = currentUserService.userRoles.permissions.includes(
    "casemanager.add_casereview"
  );
  const revalidatParent = useRevalidateParent("rvClientDetail");
  const genPainOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const [genPain, setGenPain] = useState(0);
  const [checkedInjuries, setCheckedInjuries] = useState([]);
  const [displayInjuries, setDisplayInjuries] = useState([]);
  const [responseModal, setResponseModal] = useState(false);
  const [responseBreakdown, setResponseBreakdown] = useState("");
  const [isError, setIsError] = useState(false);
  const [formValues, setFormValues] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCheckedInjuries(injury?.injuries || []);
    setDisplayInjuries(injury?.injuries || []);
    setGenPain(injury?.clientPainLevel || 0);
  }, [injury]);

  const handleSubmit = async () => {
    if (genPain === null && !checkedInjuries.length && !formValues.length) {
      alert("Please add General pain value to post");
    } else {
      setLoading(true);
      try {
        const body = {
          case: +casePk,
          client_pain_level: +genPain,
          injuries: checkedInjuries.concat(formValues || []),
        };
        await caseInjuriesRepository.createRecord(body);
        revalidatParent();
        setResponseModal(true);
        setResponseBreakdown("Pain level successfully updated");
        setIsError(false);
        setFormValues([]);
      } catch (error) {
        const errArr = ApiRequestErrorHandler(error);
        setIsError(true);
        setResponseModal(true);
        setResponseBreakdown(errArr);
      } finally {
        setLoading(false);
      }
    }
  };

  const closeResModal = () => {
    if (isError) {
      setIsError(false);
      setResponseModal(false);
      setResponseBreakdown("");
    } else {
      setIsError(false);
      setResponseModal(false);
      setResponseBreakdown("");
    }
  };

  const addFormFields = () => {
    setFormValues([...formValues, { body_part: "", pain_level: "" }]);
  };

  const removeFormFields = (i) => {
    const newFormValues = [...formValues];
    const newInjuryValues = [...checkedInjuries];
    newFormValues.splice(i, 1);
    newInjuryValues.splice(i, 1);
    setFormValues(newFormValues);
  };

  return (
    <div className="border-t pt-2">
      <p className="text-center text-[12px] font-bold">General Pain Level*</p>
      <RadioGroup
        className="flex-row justify-center"
        value={genPain}
        onChange={(e) => setGenPain(e.target.value)}
      >
        {genPainOptions.map((opt) => (
          <FormControlLabel
            value={opt}
            label={opt}
            key={opt}
            disabled={!canAddData}
            control={
              <Radio
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: 12,
                  },
                }}
              />
            }
            className="m-0 flex-col text-[10px]"
          />
        ))}
      </RadioGroup>
      <List>
        {!displayInjuries || displayInjuries.length === 0 ? (
          <ListItem className="justify-center">No Data Found</ListItem>
        ) : (
          displayInjuries.map((item, idx) => (
            <InjuryUpdateRow
              injuryItem={item}
              initialInjuryItem={injury.injuries[idx]}
              key={item.body_part + Math.random()}
              genPainOptions={genPainOptions}
              checked={checkedInjuries}
              setChecked={setCheckedInjuries}
              display={displayInjuries}
              setDisplay={setDisplayInjuries}
              canAddData={canAddData}
            />
          ))
        )}
        {formValues.map((item, idx) => (
          <InjuryAddRow
            key={idx}
            idx={idx}
            genPainOptions={genPainOptions}
            removeFormFields={removeFormFields}
            injuryItem={item}
            formValues={formValues}
            setFormValues={setFormValues}
          />
        ))}
      </List>
      <div className="mr-2 flex justify-end">
        {canAddData ? (
          <Tooltip title="Add Injury Data">
            <IconButton onClick={() => addFormFields()}>
              <ControlPointOutlined className="text-green-600" />
            </IconButton>
          </Tooltip>
        ) : (
          ""
        )}
      </div>
      {(genPain === null && !checkedInjuries.length && !formValues.length) || !canAddData ? (
        ""
      ) : (
        <FormSubmitButton
          isLoading={loading}
          handleSubmit={handleSubmit}
          buttonText="Submit"
          buttonColor="green"
          buttonId="pain-levels-submit"
        />
      )}

      <ResponseModal
        title={`${isError ? "Failed" : "Successful"}`}
        isError={isError}
        description={isError ? "" : responseBreakdown}
        openBool={responseModal}
        setOpenBool={setResponseModal}
        errorMessage={responseBreakdown}
        handleCloseFunc={closeResModal}
      />
    </div>
  );
}
